import {
  hqEmphasisLoadResultSchema,
  hqEmphasisSaveResultSchema,
  hqEmphasisSaveInputSchema,
  type HqEmphasisLoadResult,
  type HqEmphasisSaveInput,
  type HqEmphasisAttachment,
} from '@kop/contracts';
import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import {
  createHqMonthlyMeetingsRepository,
  type MeetingUploadProgress,
} from '../hq-monthly-meetings/repository';

type Period = { year: number; half: 1 | 2 };
type Transport = {
  load(input: Period): Promise<unknown>;
  save(input: HqEmphasisSaveInput): Promise<unknown>;
  evidence(
    operation: 'prepare' | 'commit' | 'open',
    input: Record<string, unknown>,
  ): Promise<unknown>;
};
const reads = createSessionKeyedReadCache<{ value: HqEmphasisLoadResult; at: number }>(8);
export function createHqEmphasisRepository(transport: Transport | Promise<Transport>, uid: string) {
  const key = ({ year, half }: Period) => `${uid}:${year}:${half}`;
  // Share only the verified binary uploader/signed-asset reader. No monthly records are read/written.
  const media = createHqMonthlyMeetingsRepository(
    {
      load: async () => {
        throw Error('EMPHASIS_MEDIA_ONLY');
      },
      save: async () => {
        throw Error('EMPHASIS_MEDIA_ONLY');
      },
      evidence: async (operation, input) => {
        const { meetingId, ...fields } = input;
        return (await transport).evidence(operation, { ...fields, eventId: meetingId });
      },
    },
    undefined,
    `emphasis:${uid}`,
  );
  return {
    peek(input: Period) {
      return reads.peek(key(input))?.value ?? null;
    },
    async load(input: Period, fresh = false) {
      const hit = reads.peek(key(input));
      try {
        return (
          await reads.read(
            key(input),
            async () => {
              const value = hqEmphasisLoadResultSchema.parse(await (await transport).load(input));
              if (
                value.year !== input.year ||
                value.half !== input.half ||
                (value.document &&
                  (value.document.year !== input.year ||
                    value.document.half !== input.half ||
                    value.document.draft.year !== input.year ||
                    value.document.draft.half !== input.half ||
                    value.document.eventId !== `emphasis-${input.year}-h${input.half}`))
              )
                throw Error('EMPHASIS_PERIOD_MISMATCH');
              return { value, at: Date.now() };
            },
            fresh || !hit || Date.now() - hit.at > 60_000,
          )
        ).value;
      } catch (error) {
        const code =
          error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
        if (
          ![
            'functions/internal',
            'functions/unavailable',
            'functions/resource-exhausted',
            'functions/deadline-exceeded',
            'functions/network-request-failed',
          ].includes(code)
        )
          reads.invalidate(key(input));
        throw error;
      }
    },
    async save(raw: HqEmphasisSaveInput) {
      const input = hqEmphasisSaveInputSchema.parse(raw);
      const result = hqEmphasisSaveResultSchema.parse(await (await transport).save(input));
      if (
        result.document.eventId !== input.eventId ||
        result.receipt.commandId !== input.commandId ||
        result.document.version !== input.expectedVersion + 1 ||
        result.receipt.version !== result.document.version ||
        result.receipt.savedAt !== result.document.savedAt ||
        JSON.stringify(result.document.draft) !== JSON.stringify(input.draft)
      )
        throw Error('저장 결과를 확인하지 못했습니다. 입력 내용을 유지했습니다.');
      reads.invalidate(key(input.draft));
      return result;
    },
    async upload(
      eventId: string,
      file: File,
      signal: AbortSignal,
      onProgress: (value: MeetingUploadProgress) => void,
    ): Promise<HqEmphasisAttachment> {
      const kind =
        /\.pdf$/iu.test(file.name) || file.type === 'application/pdf' ? 'document' : 'photo';
      return media.upload(eventId, file, kind, signal, onProgress);
    },
    open: media.open,
  };
}
export type HqEmphasisRepository = ReturnType<typeof createHqEmphasisRepository>;
export function createFirebaseHqEmphasisRepository(uid: string): HqEmphasisRepository | null {
  const config = {
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
  };
  if (
    !uid ||
    import.meta.env.VITE_AUTH_ADAPTER !== 'firebase' ||
    Object.values(config).some((value) => !value.trim())
  )
    return null;
  return createHqEmphasisRepository(
    import('@kop/operations-firebase-adapter').then((module) =>
      module.createHqEmphasisPort(config, uid),
    ),
    uid,
  );
}
